import React, { Fragment, useState } from "react";
import FlowSelector from "./FlowSelector";
import NodeList, { NODE_LIST_CONTROLLER } from "../NodeList/NodeList";
import { WORKSPACE_LEFT_LIST_ICONS } from "../v2/CustomisationWorkspace/WorkspaceAssets/Assets";
import { getSubNodes, renderNodeName } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/VisualEditorDataFormatHelperFunctions";
import { addVal, removeVal } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import { useNavigate } from "react-router-dom";
import { RIGHT_CLICK_MENU_CONTROLLER } from "../RightClickMenu/RightClickMenu";
import { evaluateDisplayConditionsForListNode } from "../v2/CustomisationWorkspace/WorkspaceComponents/ModuleList/customFields/CustomFieldRenderer";
import { getIconCategoryClass } from "../common/iconCategory";
import LottieIcon from "../common/LottieIcon";

const LayersPanel = ({ 
  layers, 
  expandedLayers, 
  onLayerToggle,
  isStackingMode,
  setIsStackingMode,
  stackingOrder,
  draggedStackItem,
  dragOverStackItem,
  onStackDragStart,
  onStackDragEnd,
  onStackDragOver,
  hoveredStackingItem,
  setHoveredStackingItem,
  isScrollingRef,
  flows,
  activeFlowId,
  onFlowChange,
  onCreateFlow,
  showExpansionLines,
  setShowExpansionLines,

  leftListNewCustomisationItems,
  selectedFeatureId,
  setSelectedFeatureId,
  setSelectedFeatureNode,
  activeNode,
  activateNode,
  clearActiveNode,
  setTimelinePlayheadStep,
  forceUpdateComponentFunc,

  campaignData,
}) => {
  const navigate = useNavigate();

  console.log("Rendering LayersPanel with layers:", layers, "expandedLayers:", expandedLayers, "isStackingMode:", isStackingMode, "stackingOrder:", stackingOrder, "leftListNewCustomisationItems:", leftListNewCustomisationItems);

  const [rightClickedNode, setRightClickedNode] = useState(null);
    const nodeListRightClicked = (e, node, nodes, onDone) => {
        setRightClickedNode(node);
        // console.log("Right click on node: ", node);
        const rightClickMenu = [];

        if (node.removable) {
            rightClickMenu.push({
                label: "Delete " + node.name,
                action: (e) => {
                    // console.log("Delete clicked for new format node: ", node);
                    removeVal(node.valsArray, node.dataIndex, null, node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    forceUpdateComponentFunc(node.parent);
                }
            });
        }

        console.log("Right click menu items for node: ", node, rightClickMenu);

        if (rightClickMenu.length) {
            e.preventDefault();
            RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, rightClickMenu, () => { NODE_LIST_CONTROLLER.refreshNodeList("customisation-workspace-left-list"); onDone(); });
            return true; // We handled the right click
        } else {
            console.warn("No right click menu items available for new format node: ", node);
            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
            return false; // We did not handle the right click
        }
    }

  return (
    <div className="layers-panel">
      {/* Flow selector above stacking order */}
      <FlowSelector
        flows={flows}
        activeFlowId={activeFlowId}
        onFlowChange={onFlowChange}
        onCreateFlow={onCreateFlow}
      />
      
      <div className="layers-header">
        <label className="stacking-toggle">
          <span className="toggle-label">Reorder Stacking</span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              checked={isStackingMode}
              onChange={(e) => setIsStackingMode(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
        {isStackingMode && (
          <div className="stacking-hint">
            Drag to reorder z-index (bottom to top)
          </div>
        )}
        
        <label className="stacking-toggle" style={{ marginTop: '12px' }}>
          <span className="toggle-label">Show Expansion Lines</span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              checked={showExpansionLines}
              onChange={(e) => setShowExpansionLines(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
      </div>
      
      <div className={`layers-list ${draggedStackItem !== null ? 'is-dragging' : ''}`}>
        {!isStackingMode ? (
          // Normal hierarchical view
          layers.length === 0 ? (
            <div className="empty-layers">
              No layers added.
            </div>
          ) : (
            layers.map((layer, index) => {
              if (layer.isStep) {
                const isExpanded = expandedLayers.has(layer.id);
                // Get all components that belong to this step (including expanded copies for expanded components)
                const childComponents = layers.filter(l => {
                  if (!l.isComponent) return false;
                  // Include components whose parent is this step
                  return l.parentStep === layer.id;
                });
                
                return (
                  <div key={layer.id} className="layer-item">
                    <div 
                      className="layer-content step-layer" 
                      onClick={() => onLayerToggle(layer.id)}
                    >
                      {childComponents.length > 0 && (
                        <span className={`layer-dropdown-arrow ${isExpanded ? 'expanded' : ''}`}>
                          ▶
                        </span>
                      )}
                      <span className={`layer-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(layer.iconCategory) ? layer.iconCategory[0] : layer.iconCategory)}`}><LottieIcon icon={layer.icon} varKey={layer.varKey} size={24} /></span>
                      <span className="layer-name">{layer.name}</span>
                    </div>
                    {isExpanded && childComponents.length > 0 && (
                      <div className="layer-nested">
                        {childComponents.map((component) => {
                          const listId = 'customisation-workspace-left-list_' + component.featureId + '_' + component.id + (component.copyId ? ('_' + component.copyId) : '');
                          const listNode = leftListNewCustomisationItems?.[component.featureId]
                            || (leftListNewCustomisationItems && component.varKey
                                ? Object.values(leftListNewCustomisationItems).find(n => n.varKey === component.varKey)
                                : null);
                          if (!listNode) return null;
                          // Patch the stale featureId so future lookups on the same object hit the fast path
                          if (listNode.id && component.featureId !== listNode.id) {
                            component.featureId = listNode.id;
                          }
                          return (
                            <Fragment key={component.id + (component.copyId ? ('_' + component.copyId) : '')}>
                              {/* <div key={component.copyId || component.id} className={`layer-child ${component.isExpandedCopy ? 'expanded-copy' : ''}`}> */}
                              {/* <span className="layer-icon">{component.icon}</span>
                              <span className="layer-name">{component.name}</span> */}
                                          <NodeList
                                              id={listId}
                                              nodes={[listNode] || []}
                  
                                              indentation='1.5rem'

                                              clickAnywhereToExpand={false}
                  
                                              expandNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(0deg)' }}>▶</span>}
                                              collapseNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(90deg)' }}>▶</span>}
                  
                                              renderNodeName={(nodeName, node, nodes, isExpandable) => {
                                                  if (node.depth === 0) {
                                                      return (
                                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                                                              {/*isExpandable && (
                                                                  <span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: node.expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                                              )*/}
                                                                <span className={`layer-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(component.iconCategory) ? component.iconCategory[0] : component.iconCategory)}`}>
                                                                  <LottieIcon icon={component.icon} varKey={component.varKey} size={20} />
                                                              </span>
                                                              <span className='v2-workspace__left-list-text'>{nodeName}</span>
                                                          </span>
                                                      );
                                                  }
                                                  return renderNodeName(nodeName, node, nodes, isExpandable);
                                              }}
                                              getSubNodes={getSubNodes}

                                              checkNodeVisibility={
                                                (node, nodes) => {
                                                  // console.log("Checking visibility for node: ", node, nodes);
                                                  if (node.depth === 0) {
                                                    return true; // Always show top-level nodes (steps)
                                                  }

                                                  return evaluateDisplayConditionsForListNode(node, campaignData);
                                                }
                                              }
                  
                                              onNodeClick={(e, node, nodes) => {
                                                  // console.log("Node clicked:", node);
                                                  if (node.openLegacyEditor) {
                                                    activateNode(node);
                                                  } else
                                                  if (node.openUrl) {
                                                      navigate(node.urlToOpen);
                                                  } else
                                                  if (node.isAddButton) {
                                                      // console.log("Add button clicked - add new node:", node);
                                                      addVal(node.valsArray, node.varSchema.valSchema, node.featureData.feature, null, node);
                                                  } else
                                                  if (node.depth === 0) {
                                                      // Toggle expand/collapse on name/icon click
                                                      if (node.hasChildren && node.subNodes && node.subNodes.length > 0) {
                                                          node.expanded = !node.expanded;
                                                          NODE_LIST_CONTROLLER.refreshNodeList(listId);
                                                      }
                                                      // console.log("Node clicked - select node:", node);
                                                      if (selectedFeatureId !== node.id) {
                                                          setSelectedFeatureId(node.id);
                                                          setSelectedFeatureNode(node);
                                                          
                                                          // Find a visualised group node in the subNodes if we have one
                                                          const visualisedGroupNode = node.subNodes.find(subNode => (subNode.isVisualised || subNode?.config?.groupSettings?.visualise) && subNode.config.visualisationConfig.startStep === 0);
                                                          if (visualisedGroupNode) {
                                                              activateNode(visualisedGroupNode);
                                                              setTimelinePlayheadStep(visualisedGroupNode.config.visualisationConfig.startStep || 0);
                                                          } else {
                                                              clearActiveNode();
                                                          }
                  
                                                          setTimelinePlayheadStep(0);
                                                      }
                                                      // Return true to select this node
                                                      return true;
                                                  } else if (node.topNode) {
                                                      // console.log("Node clicked - select top node:", node.topNode);
                                                      if (selectedFeatureId !== node.topNode.id) {
                                                          setSelectedFeatureId(node.topNode.id);
                                                          setSelectedFeatureNode(node.topNode);
                                                          NODE_LIST_CONTROLLER.selectNodeByIdAndPath(listId, node.topNode.id, node.topNode.path, false, false, false);
                                                      }
                                                      if (!node.active) {
                                                          activateNode(node);
                                                      }
                                                  }
                                                  forceUpdateComponentFunc();
                                                  return false;
                                              }}
                  
                                              enableRightClickMenu={true}
                                              onNodeRightClick={(e, node, nodes, onDone) => {
                                                  return nodeListRightClicked(e, node, nodes, onDone);
                                              }}

                                              className={selectedFeatureId && selectedFeatureId !== listNode?.id ? 'unselected-node-list' : null}
                                          />
                            {/* </div> */}
                            </Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null; // Component layers are rendered as children of steps
            })
          )
        ) : (
          // Stacking order view (flat list)
          stackingOrder.length === 0 ? (
            <div className="empty-layers">
              No components to stack.
            </div>
          ) : (
            <>
              {stackingOrder.map((item, index) => {
                const isDragging = draggedStackItem === index;
                const showDropIndicator = !isDragging && dragOverStackItem === index && draggedStackItem !== null;
                
                const handleDragStartSimple = (e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  onStackDragStart(index, item.id);
                };
                
                const handleDrop = (e) => {
                  e.preventDefault();
                  // Immediately hide the drag ghost
                  e.dataTransfer.dropEffect = 'none';
                };
                
                return (
                  <React.Fragment key={item.id}>
                    {showDropIndicator && (
                      <div className="stacking-drop-indicator">
                        <div className="drop-line"></div>
                      </div>
                    )}
                    <div 
                      className={`stacking-item ${isDragging ? 'dragging' : ''}`}
                      draggable={true}
                      onDragStart={handleDragStartSimple}
                      onDragEnd={onStackDragEnd}
                      onDragOver={(e) => onStackDragOver(e, index)}
                      onDrop={handleDrop}
                      onMouseEnter={() => {
                        const targetId = item.featureId || item.id;
                        console.log('[LayersPanel] Hover stacking item', targetId, item.name);
                        setHoveredStackingItem(targetId);
                      }}
                      onMouseLeave={() => setHoveredStackingItem(null)}
                    >
                      <div className="stacking-drag-handle">⋮⋮</div>
                      <span className="stacking-icon"><LottieIcon icon={item.icon} varKey={item.varKey} size={20} /></span>
                      <span className="stacking-name">{item.name}</span>
                    </div>
                  </React.Fragment>
                );
              })}
              {/* Drop zone at the end of the list */}
              {draggedStackItem !== null && (
                <div 
                  className="stacking-drop-zone-end"
                  onDragOver={(e) => onStackDragOver(e, stackingOrder.length)}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'none';
                  }}
                >
                  {dragOverStackItem === stackingOrder.length && (
                    <div className="stacking-drop-indicator">
                      <div className="drop-line"></div>
                    </div>
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
